import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { Router, RouterModule } from '@angular/router'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { ToastrService } from 'ngx-toastr'
import { ClockInClockOutService } from '../../../../services/clockIn-clockOut.service'
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs'
import { environment } from '../../../../../environments/environment'
import { eps } from '../../../../services/url.service'
import {
  getDaysInPeriod,
  getInitials,
  getUpdatedEffectiveFromDate
} from '../../../../utils/payroll.utils'
import { selectRunningPayroll } from '../../../../store/contractor-payroll/contractor-payroll.reducers'
import { loadBusinesses } from '../../../../store/business/business.actions'
import {
  ContractorPayroll,
  ContractorPayrollStatus,
  IncomingContractorPayroll,
  IncomingRunningPayrollBatch,
  RecalculatePayrollRequest,
  RunningPayrollBatch
} from '../../../../store/contractor-payroll/contractor-payroll.state'
import Papa from 'papaparse'
import { runningCPActions } from '../../../../store/contractor-payroll/contractor-payroll.actions'
import { selectBusinesses } from '../../../../store/business/business.reducer'
import { CommonModule } from '@angular/common'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { KeySettingsComponent } from '../key-settings/key-settings.component'
import { selectUser } from '../../../../store/auth/auth.state'
import { InvoiceComponent } from '../invoice/invoice.component'
import { ContractorPayrollService } from '../../../../services/contractor-payroll.service'
import { Basis } from '../../../../store/payout-agreement/payout-agreement.state'

interface PayrollOverrideRow {
  empId: string
  grossPayout?: string
  netPayout?: string
  tds?: string
  lopDays?: string
  totalHours?: string
  bonus?: string
  expenses?: string
  adjustments?: string
  rounding?: string
}

@Component({
  selector: 'app-earnings',
  imports: [RouterModule, CommonModule, ReactiveFormsModule, CapitalizePipe],
  templateUrl: './earnings.component.html',
  styleUrl: './earnings.component.scss'
})
export class EarningsComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private router = inject(Router)
  private toastr = inject(ToastrService)
  private fb = inject(FormBuilder)
  private modalService = inject(NgbModal)
  private ClockInClockOutService = inject(ClockInClockOutService)
  private ContractorPayrollService = inject(ContractorPayrollService)
  private destroy$ = new Subject<void>()
  @Output() nextScreen = new EventEmitter<boolean>()
  KeySettingsComponent = KeySettingsComponent
  InvoiceComponent = InvoiceComponent
  tdsPercentValues = [1, 5, 10, 15, 20, 25, 30]
  imageBaseUrl = environment.apiBaseUrl + '/uploads/'
  eps = eps

  //Utils and helpers
  getInitials = getInitials
  getUpdatedEffectiveFromDate = getUpdatedEffectiveFromDate

  payrollBatchForm!: FormGroup
  runningPayroll$ = this.store.select(selectRunningPayroll)
  currentIncomingPayrolls: {
    fixed: IncomingContractorPayroll[]
    hourly: IncomingContractorPayroll[]
  } = { fixed: [], hourly: [] }
  payPeriodStartDate = ''
  payPeriodEndDate = ''
  businessEntity$!: Observable<string | undefined>
  payoutFrequency = ''

  changeScreen(value: boolean) {
    this.nextScreen.emit(value)
  }

  get fixedPayrollsFormArray(): FormArray {
    return this.payrollBatchForm.get('fixedPayrolls') as FormArray
  }
  get hourlyPayrollsFormArray(): FormArray {
    return this.payrollBatchForm.get('hourlyPayrolls') as FormArray
  }

  ngOnInit(): void {
    this.initializeForm()
    this.store.dispatch(loadBusinesses({}))
    this.triggerInitialDataLoad()
    this.initializeBusinessEntityObservable()
    this.subscribeToDataAndBuildForm()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private initializeForm(): void {
    this.payrollBatchForm = this.fb.group({
      fixedPayrolls: this.fb.array([]),
      hourlyPayrolls: this.fb.array([])
    })
  }

  private triggerInitialDataLoad(): void {
    this.runningPayroll$
      .pipe(
        filter(
          (rp): rp is IncomingRunningPayrollBatch =>
            !!rp && !!rp.payPeriodStart && !!rp.payPeriodEnd && !!rp.belongsTo
        ),
        take(1),
        tap(rp => {
          this.store.dispatch(
            runningCPActions.loadRunningPayroll({
              payPeriodStart: rp.payPeriodStart,
              payPeriodEnd: rp.payPeriodEnd,
              belongsTo: rp.belongsTo,
              payoutFrequency: rp.payoutFrequency
            })
          )
        })
      )
      .subscribe()
  }

  private subscribeToDataAndBuildForm(): void {
    this.runningPayroll$
      .pipe(
        filter(rp => !!rp && !!rp.contractorPayrolls),
        tap(rp => {
          this.currentIncomingPayrolls = {
            fixed: [...(rp!.contractorPayrolls.fixed ?? [])],
            hourly: [...(rp!.contractorPayrolls.hourly ?? [])]
          }
          this.payPeriodStartDate = rp!.payPeriodStart
          this.payPeriodEndDate = rp!.payPeriodEnd
          this.payoutFrequency = rp!.payoutFrequency
        }),
        map(rp => rp!.contractorPayrolls!),
        takeUntil(this.destroy$)
      )
      .subscribe(payrolls => {
        this.fixedPayrollsFormArray.clear({ emitEvent: false })
        this.hourlyPayrollsFormArray.clear({ emitEvent: false })

        payrolls.fixed?.forEach(p =>
          this.fixedPayrollsFormArray.push(this.createPayrollFG(p), { emitEvent: false })
        )
        payrolls.hourly?.forEach(p =>
          this.hourlyPayrollsFormArray.push(this.createPayrollFG(p), { emitEvent: false })
        )

        this.payrollBatchForm.updateValueAndValidity()
        this.payrollBatchForm.markAsPristine()
      })
  }

  /** Creates a single FormGroup for a contractor's payroll and sets up its recalculation logic. */
  private createPayrollFG(incomingPayroll: IncomingContractorPayroll): FormGroup {
    const isHourly = incomingPayroll.basis === Basis.HOURLY

    const fg = this.fb.group({
      // Common fields
      id: [incomingPayroll.id],
      empId: [incomingPayroll.employee.empId],
      employeeId: [incomingPayroll.employeeId, Validators.required],
      belongsTo: [incomingPayroll.belongsTo, Validators.required],
      payoutAgreementId: [incomingPayroll.payoutAgreementId, Validators.required],
      basis: [incomingPayroll.basis],
      bonus: [incomingPayroll.bonus ?? 0, Validators.min(0)],
      expenses: [incomingPayroll.expenses ?? 0, Validators.min(0)],
      adjustments: [incomingPayroll.adjustments ?? 0],
      rounding: [incomingPayroll.rounding ?? 0],
      status: [incomingPayroll.status, Validators.required],
      tds: [incomingPayroll.tds ?? 0, Validators.min(0)],
      grossPayout: [incomingPayroll.grossPayout ?? 0],
      netPayout: [incomingPayroll.netPayout ?? 0],
      // Conditional fields
      lopDays: [isHourly ? 0 : (incomingPayroll.lopDays ?? 0)],
      totalHours: [isHourly ? (incomingPayroll.totalHours ?? 0) : 0, Validators.min(0)]
    })

    // Recalculate via backend on value changes
    fg.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        switchMap(formValue => {
          const requestBody: RecalculatePayrollRequest = {
            payoutAgreementId: formValue.payoutAgreementId ?? '',
            belongsTo: formValue.belongsTo ?? '',
            payPeriodStart: this.payPeriodStartDate,
            payPeriodEnd: this.payPeriodEndDate,
            inputs: {
              tds: formValue.tds ?? 0,
              bonus: formValue.bonus ?? 0,
              expenses: formValue.expenses ?? 0,
              adjustments: formValue.adjustments ?? 0,
              rounding: formValue.rounding ?? 0,
              // Send the correct input based on basis
              lopDays: isHourly ? 0 : (formValue.lopDays ?? 0),
              totalHours: isHourly ? (formValue.totalHours ?? 0) : 0
            }
          }
          return this.ContractorPayrollService.recalculate(requestBody)
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((updatedPayroll: IncomingContractorPayroll) => {
        fg.patchValue(
          {
            grossPayout: updatedPayroll.grossPayout,
            netPayout: updatedPayroll.netPayout
          },
          { emitEvent: false }
        )
        // Update the correct reference array
        const refArray = isHourly
          ? this.currentIncomingPayrolls.hourly
          : this.currentIncomingPayrolls.fixed
        const formIndex = refArray.findIndex(p => p.employeeId === fg.value.employeeId)
        if (formIndex !== -1) refArray[formIndex] = updatedPayroll
      })

    return fg
  }

  onTdsPercentChange(event: Event, payrollCtrl: AbstractControl): void {
    const selectElement = event.target as HTMLSelectElement
    const tdsPercent = parseFloat(selectElement.value)

    if (isNaN(tdsPercent)) {
      payrollCtrl.get('tds')?.patchValue(0)
      return
    }

    const grossPayout = payrollCtrl.get('grossPayout')?.value ?? 0
    const newTdsAmount = (grossPayout * tdsPercent) / 100

    payrollCtrl.get('tds')?.patchValue(parseFloat(newTdsAmount.toFixed(2)))
  }

  syncLopDays(): void {
    this.runningPayroll$
      .pipe(
        filter(
          (rp): rp is IncomingRunningPayrollBatch =>
            !!rp && !!rp.belongsTo && !!rp.payPeriodStart && !!rp.payPeriodEnd
        ),
        take(1),
        switchMap(batch => {
          //Instead of employees make it for contractor or filter if it gets of both (employee and contractor)
          return this.ClockInClockOutService.getLopDaysForEmployees(
            batch.payPeriodStart,
            batch.payPeriodEnd,
            batch.belongsTo
          )
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: lopDataFromServer => {
          let updatedCount = 0
          this.fixedPayrollsFormArray.controls.forEach(control => {
            const employeeId = control.get('employeeId')?.value
            const lopEntry = lopDataFromServer.find(lop => lop.employeeId === employeeId)
            if (lopEntry && control.get('lopDays')?.value !== lopEntry.lopDays) {
              control.get('lopDays')?.patchValue(lopEntry.lopDays)
              updatedCount++
            }
          })
          if (updatedCount > 0) this.payrollBatchForm.markAsDirty()
        },
        error: err => this.toastr.error('Error syncing LOP days.', err.message)
      })
  }

  onSubmit(goToNextScreen: boolean): void {
    if (this.payrollBatchForm.invalid) {
      this.payrollBatchForm.markAllAsTouched()
      return
    }
    const round2 = (num: number) => Number(num.toFixed(2))
    this.runningPayroll$.pipe(take(1)).subscribe(currentBatch => {
      if (!currentBatch) return
      const rawFixedPayrolls = this.fixedPayrollsFormArray.getRawValue()
      const rawHourlyPayrolls = this.hourlyPayrollsFormArray.getRawValue()
      const allOriginalPayrolls = [
        ...this.currentIncomingPayrolls.fixed,
        ...this.currentIncomingPayrolls.hourly
      ]
      // allOriginalPayrolls.forEach(payroll => {
      //   if (payroll.calculationBreakdown?.grossForPeriod !== undefined) {
      //     payroll.calculationBreakdown.grossForPeriod = round2(
      //       payroll.calculationBreakdown.grossForPeriod
      //     )
      //   }
      // })
      allOriginalPayrolls.forEach(({ calculationBreakdown: cb }) => {
        if (!cb) return

        cb.grossForPeriod = round2(cb.grossForPeriod)

        if (cb.prorationDetails) {
          cb.prorationDetails.period1 &&
            (cb.prorationDetails.period1.gross = round2(cb.prorationDetails.period1.gross))

          cb.prorationDetails.period2 &&
            (cb.prorationDetails.period2.gross = round2(cb.prorationDetails.period2.gross))
        }
      })
      const payrollMapper = (p: ContractorPayroll & { empId: string }): ContractorPayroll => {
        const originalPayroll = allOriginalPayrolls.find(d => d.employeeId === p.employeeId)
        const { empId, ...payroll } = p

        return {
          ...payroll,
          calculationBreakdown: originalPayroll?.calculationBreakdown!,
          status: goToNextScreen
            ? ContractorPayrollStatus.IN_PROCESS
            : ContractorPayrollStatus.DRAFT
        }
      }

      const processedFixedPayrolls = rawFixedPayrolls.map(payrollMapper)
      const processedHourlyPayrolls = rawHourlyPayrolls.map(payrollMapper)

      const allProcessedPayrolls = [...processedFixedPayrolls, ...processedHourlyPayrolls]
      const totalAmount = allProcessedPayrolls.reduce((sum, p) => sum + (p.grossPayout ?? 0), 0)
      const totalTds = allProcessedPayrolls.reduce((sum, p) => sum + Math.ceil(p.tds ?? 0), 0)
      const totalNetAmount = allProcessedPayrolls.reduce((sum, p) => sum + (p.netPayout ?? 0), 0)
      // Get the user id of the current logged-in user to log who ran the current payroll
      let userId = ''
      this.store
        .select(selectUser)
        .pipe(take(1))
        .subscribe(user => {
          if (!user) return
          userId = user.id
        })
      const batchData: RunningPayrollBatch = {
        id: currentBatch.id,
        belongsTo: currentBatch.belongsTo,
        payPeriodStart: currentBatch.payPeriodStart,
        payPeriodEnd: currentBatch.payPeriodEnd,
        payoutFrequency: currentBatch.payoutFrequency,
        contractorPayrolls: {
          fixed: processedFixedPayrolls,
          hourly: processedHourlyPayrolls
        },
        totalAmount: Number(totalAmount.toFixed(2)),
        totalTds: Math.ceil(Number(totalTds.toFixed(2))),
        netAmount: Number(totalNetAmount.toFixed(2)),
        contractorCount: allProcessedPayrolls.length,
        status: goToNextScreen ? ContractorPayrollStatus.IN_PROCESS : ContractorPayrollStatus.DRAFT,
        processedBy: goToNextScreen ? userId : null,
        paidBy: null
      }
      this.store.dispatch(runningCPActions.saveRunningCPBatch({ batchData }))
      if (goToNextScreen) {
        this.nextScreen.emit(true)
      } else {
        this.router.navigate([eps.hrm.manageContractorPayrollPath])
      }
    })
  }

  openModal(modal: unknown, index: number, hourlyBasis: boolean) {
    const fg = hourlyBasis
      ? (this.hourlyPayrollsFormArray.at(index) as FormGroup)
      : (this.fixedPayrollsFormArray.at(index) as FormGroup)
    const originalPayroll = hourlyBasis
      ? this.currentIncomingPayrolls.hourly[index]
      : this.currentIncomingPayrolls.fixed[index]
    if (!fg || !originalPayroll) {
      this.toastr.error('Could not find payroll data for modal.')
      return
    }
    const modalRef = this.modalService.open(modal, {
      size: 'lg',
      centered: true,
      scrollable: false
    })
    const formValues = fg.getRawValue()
    modalRef.componentInstance.payrollData = { ...originalPayroll, ...formValues }
    modalRef.componentInstance.payPeriodStartDate = this.payPeriodStartDate
    modalRef.componentInstance.payPeriodEndDate = this.payPeriodEndDate
  }

  onLopInput(payrollCtrl: AbstractControl) {
    const value = payrollCtrl.get('lopDays')?.value
    const daysInPayPeriod = getDaysInPeriod(
      new Date(this.payPeriodStartDate),
      new Date(this.payPeriodEndDate)
    )
    if (value > 31) {
      payrollCtrl.get('lopDays')?.setValue(daysInPayPeriod)
    } else if (value < 0) {
      payrollCtrl.get('lopDays')?.setValue(0)
    }
  }

  private initializeBusinessEntityObservable(): void {
    this.businessEntity$ = combineLatest([
      this.runningPayroll$.pipe(filter((rp): rp is IncomingRunningPayrollBatch => !!rp)),
      this.store
        .select(selectBusinesses)
        .pipe(filter(businesses => !!businesses && businesses.length > 0))
    ]).pipe(
      map(([runningPayroll, businesses]) => {
        return businesses.find(b => b.id === runningPayroll.belongsTo)?.businessName
      }),
      takeUntil(this.destroy$)
    )
  }

  onFileSelect(ev: Event): void {
    const e = ev.currentTarget as HTMLInputElement
    const fileList: FileList | null = e.files
    if (fileList && fileList.length > 0) {
      const file = fileList[0]
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        this.toastr.error('Invalid file type. Please upload a CSV file.')
        return
      }
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: result => {
          if (!result.meta.fields?.includes('empId')) {
            this.toastr.error(
              'CSV file is missing the required "empId" header.',
              'Invalid CSV Header'
            )
            return
          }
          this.processOverrideUpload(result.data as PayrollOverrideRow[])
        },
        error: error => this.toastr.error('Error parsing CSV file.', error.message)
      })
      e.value = ''
    }
  }

  private processOverrideUpload(data: PayrollOverrideRow[]): void {
    let updatedCount = 0
    let notFoundCount = 0
    const notFoundIds: string[] = []

    data.forEach(row => {
      const empIdFromCsv = row.empId?.trim()
      if (!empIdFromCsv) return

      const c =
        this.fixedPayrollsFormArray.controls.find(c => c.get('empId')?.value === empIdFromCsv) ||
        this.hourlyPayrollsFormArray.controls.find(c => c.get('empId')?.value === empIdFromCsv)

      if (c) {
        const parse = (val: string | undefined) => (val?.trim() ? parseFloat(val) : NaN)

        const f = {
          lopDays: parse(row.lopDays),
          totalHours: parse(row.totalHours),
          bonus: parse(row.bonus),
          expenses: parse(row.expenses),
          adjustments: parse(row.adjustments),
          rounding: parse(row.rounding),
          grossPayout: parse(row.grossPayout),
          netPayout: parse(row.netPayout),
          tds: parse(row.tds)
        }

        const cp: Record<string, number | null> = Object.entries(f)
          // eslint-disable-next-line @typescript-eslint/naming-convention
          .filter(([_, value]) => !isNaN(value))
          .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})

        //if (Object.prototype.hasOwnProperty.call(cp, 'grossPayout')) {
        //  cp['calculationBreakdown'] = null
        //}

        if (Object.keys(cp).length > 0) {
          c.patchValue(cp, { emitEvent: false })
          updatedCount++
        }
      } else {
        notFoundCount++
        notFoundIds.push(empIdFromCsv)
      }
    })

    // Provide feedback
    if (updatedCount > 0) {
      this.payrollBatchForm.markAsDirty()
      this.toastr.success(
        `Successfully overrode data for ${updatedCount} employee(s). Their values are now locked until manually edited.`
      )
    }
    if (notFoundCount > 0) {
      this.toastr.warning(
        `${notFoundCount} Employee ID(s) from the CSV were not found in this payroll.`
      )
    }
  }
}
